import type { ModuleTone } from '../../shared/ui';

/**
 * The landing page's data, in the order of the client's brief ("Landing Page
 * Explained.docx", 2026-10-13). The order is the client's, not ours: it is not
 * the tab bar's order and must not be "aligned" with it.
 */
export interface LandingService {
  module: ModuleTone;
  icon: string;
  titleKey: string;
  descriptionKey: string;
}

export const SERVICES: readonly LandingService[] = [
  {
    module: 'announcements',
    icon: 'campaign',
    titleKey: 'landing.serviceAnnouncementsTitle',
    descriptionKey: 'landing.serviceAnnouncementsDescription',
  },
  {
    module: 'complaints',
    icon: 'forum',
    titleKey: 'landing.serviceComplaintsTitle',
    descriptionKey: 'landing.serviceComplaintsDescription',
  },
  {
    module: 'events',
    icon: 'calendar_today',
    titleKey: 'landing.serviceEventsTitle',
    descriptionKey: 'landing.serviceEventsDescription',
  },
  {
    module: 'restaurant',
    icon: 'restaurant',
    titleKey: 'landing.serviceRestaurantTitle',
    descriptionKey: 'landing.serviceRestaurantDescription',
  },
  {
    module: 'library',
    icon: 'local_library',
    titleKey: 'landing.serviceLibraryTitle',
    descriptionKey: 'landing.serviceLibraryDescription',
  },
  {
    module: 'carpooling',
    icon: 'directions_car',
    titleKey: 'landing.serviceCarpoolingTitle',
    descriptionKey: 'landing.serviceCarpoolingDescription',
  },
];

export interface LandingSocial {
  id: 'tiktok' | 'instagram' | 'facebook';
  labelKey: string;
  handleKey: string;
  /** Always an address somebody gave us: a guessed URL can be someone else's page. */
  href: string;
}

export const SOCIALS: readonly LandingSocial[] = [
  {
    id: 'tiktok',
    labelKey: 'landing.socialTikTokLabel',
    handleKey: 'landing.socialTikTokHandle',
    href: 'https://www.tiktok.com/@helios.iscae',
  },
  {
    id: 'instagram',
    labelKey: 'landing.socialInstagramLabel',
    handleKey: 'landing.socialInstagramHandle',
    href: 'https://www.instagram.com/ade.iscaecasa',
  },
  {
    id: 'facebook',
    labelKey: 'landing.socialFacebookLabel',
    handleKey: 'landing.socialFacebookHandle',
    // The brief names the page ("Ade Helios"); Reda supplied the address on 2026-10-14.
    href: 'https://www.facebook.com/profile.php?id=61590596380521',
  },
];

/** 2560x1700 original; the intrinsic size reserves the box so nothing shifts. */
export const HERO_IMAGE = {
  src: '/landing/iscae-800.webp',
  srcSet: '/landing/iscae-800.webp 800w, /landing/iscae-1600.webp 1600w',
  sizes: '(min-width: 1024px) 768px, (min-width: 736px) 672px, 100vw',
  /** What the full-screen view loads: the larger derivative, never the original. */
  full: '/landing/iscae-1600.webp',
  width: 1600,
  height: 1063,
} as const;

export const HELIOS_LOGO = {
  src: '/landing/helios-96.png',
  srcSet: '/landing/helios-96.png 1x, /landing/helios-192.png 2x',
} as const;

/**
 * The seven Helios 26-27 photos Reda supplied on 2026-10-18 (iPhone HEIC,
 * derived to 960x1280 WebP, metadata dropped). All portrait 3:4, shown in a
 * 4:3 box: the faces sit at about 40% of the height, which a centred crop keeps.
 */
export const SLIDES = [
  { src: '/landing/slide-helios-1-1280.webp', width: 960, height: 1280 },
  { src: '/landing/slide-helios-2-1280.webp', width: 960, height: 1280 },
  { src: '/landing/slide-helios-3-1280.webp', width: 960, height: 1280 },
  { src: '/landing/slide-helios-4-1280.webp', width: 960, height: 1280 },
  { src: '/landing/slide-helios-5-1280.webp', width: 960, height: 1280 },
  { src: '/landing/slide-helios-6-1280.webp', width: 960, height: 1280 },
  { src: '/landing/slide-helios-7-1280.webp', width: 960, height: 1280 },
] as const;
